// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { OrderSyncSignal } from '@/components/order-sync-signal';
import { resyncPurchaseOrder } from '@/purchase-order/api/resync-purchase-order';
import { isCancelled, isReceived } from '@/purchase-order/constants/status';
import { type POStatus } from '@/purchase-order/constants/status';
import {
  purchaseOrderDetailQueryKey,
  purchaseOrdersQueryKey,
} from '@/purchase-order/query-keys';
import { DateTime } from 'luxon';

interface Props {
  purchaseOrderKey: string;
  // The PO number, used to estimate resync time from its shipments
  purchaseOrderNumber: string;
  status: POStatus;
  // Hardfin's last poll of the 3PL for this purchase order
  lastSyncedAt: DateTime | null;
  // The 3PL's last reported data update for this purchase order
  lastThirdPartyUpdateAt: DateTime | null;
  // Whether the 3PL binding is currently in an error state
  hasSyncError: boolean;
  // The latest 3PL sync error text (empty when there is none)
  syncErrorMessage: string;
}

/**
 * 3PL sync-status signal for a purchase order: the shared OrderSyncSignal wired
 * to the purchase order resync API and query keys. The order is terminal once
 * received or cancelled, and "Sent to 3PL" applies while it awaits a receive.
 */
export function PurchaseOrderSyncSignal(props: Props) {
  // The order is terminal once received or cancelled; the icon greys and the
  // "next check" line is hidden.
  const isTerminal = isReceived(props.status) || isCancelled(props.status);
  // "Sent to 3PL" applies only while the order is still awaiting a receive; once
  // receiving activity begins (or the order becomes terminal) it reads
  // "Synced from 3PL".
  const isAwaiting =
    !isTerminal &&
    (props.status === 'AWAITING_3PL' || props.status.startsWith('OPEN_'));

  return (
    <OrderSyncSignal
      orderType="purchase"
      orderKey={props.purchaseOrderKey}
      orderNumber={props.purchaseOrderNumber}
      isTerminal={isTerminal}
      isAwaiting={isAwaiting}
      lastSyncedAt={props.lastSyncedAt}
      lastThirdPartyUpdateAt={props.lastThirdPartyUpdateAt}
      hasSyncError={props.hasSyncError}
      syncErrorMessage={props.syncErrorMessage}
      resyncOrder={resyncPurchaseOrder}
      detailQueryKey={purchaseOrderDetailQueryKey(props.purchaseOrderKey)}
      listQueryKey={purchaseOrdersQueryKey}
    />
  );
}
